// @ts-nocheck
import { Box, Skeleton } from '@mui/material';
import { WarningCircle } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XGInput } from 'xg-cl';
import {
  getTenantSettings,
  setTenantSettings
} from '../../../services/tenant/tenant';
import { ErrorToast } from '../../../utils/alerts';
import PropTypes from 'prop-types';
import './BrandingPanel.css';

const SETTINGCODE = 'GLOBALCONTACT';
const CONTACT_FIELDS = ['contactName', 'contactEmail', 'contactPhone'];
const EMPTY_CONTACT_SETTINGS = {
  contactName: '',
  contactEmail: '',
  contactPhone: ''
};

const parseContactSettings = (settingValue) => {
  if (!settingValue) {
    return EMPTY_CONTACT_SETTINGS;
  }

  try {
    const parsedValue = JSON.parse(settingValue);
    const contactSettings = Array.isArray(parsedValue)
      ? parsedValue?.[0]
      : parsedValue;

    return CONTACT_FIELDS.reduce(
      (acc, fieldName) => ({
        ...acc,
        [fieldName]: contactSettings?.[fieldName] || ''
      }),
      {}
    );
  } catch (error) {
    return EMPTY_CONTACT_SETTINGS;
  }
};

const buildContactPayload = (contactSettings) => {
  return CONTACT_FIELDS.reduce((acc, fieldName) => {
    const value = contactSettings?.[fieldName]?.trim();
    if (value) {
      acc[fieldName] = value;
    }
    return acc;
  }, {});
};

const isValidEmail = (email) => {
  if (!email) {
    return true;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const BrandingPanel = ({ id }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [contactSettings, setContactSettings] = useState(
    EMPTY_CONTACT_SETTINGS
  );
  const [savedContactSettings, setSavedContactSettings] = useState(
    EMPTY_CONTACT_SETTINGS
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);

  const fieldConfig = useMemo(
    () => [
      {
        name: 'contactName',
        type: 'text',
        label: t('settings:contactName', 'Contact name'),
        helperText: t(
          'settings:contactNameHelperText',
          "This name appears in email closings. If left empty, the trip creator's name is used."
        ),
        placeholder: t('settings:enterContactName', 'Enter contact name')
      },
      {
        name: 'contactEmail',
        type: 'email',
        label: t('settings:contactEmail', 'Contact email'),
        placeholder: t('settings:enterContactEmail', 'Enter contact email')
      },
      {
        name: 'contactPhone',
        type: 'tel',
        label: t('settings:contactPhone', 'Contact phone'),
        placeholder: t('settings:enterContactPhone', 'Enter contact phone')
      }
    ],
    [t]
  );

  useEffect(() => {
    const fetchBrandingSettings = async () => {
      setIsLoading(true);

      try {
        const response = await getTenantSettings(SETTINGCODE);
        const parsedContactSettings = parseContactSettings(
          response?.data?.settingvalue
        );

        setContactSettings(parsedContactSettings);
        setSavedContactSettings(parsedContactSettings);
      } catch (error) {
        ErrorToast(
          t('common:errorLoadingSettings', {
            defaultValue: 'Error Loading Settings'
          })
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrandingSettings();
  }, [t]);

  const updateFieldError = (fieldName, errorMessage = '') => {
    setFieldErrors((prevState) => ({
      ...prevState,
      [fieldName]: errorMessage
    }));
  };

  const handleFieldChange = (fieldName, value) => {
    setContactSettings((prevState) => ({
      ...prevState,
      [fieldName]: value
    }));

    updateFieldError(fieldName);
  };

  const handleFieldBlur = async (fieldName) => {
    if (savingField) {
      return;
    }

    const nextValue = contactSettings?.[fieldName]?.trim() || '';
    const previousValue = savedContactSettings?.[fieldName] || '';

    if (nextValue === previousValue) {
      return;
    }

    if (fieldName === 'contactEmail' && !isValidEmail(nextValue)) {
      updateFieldError(
        fieldName,
        t('settings:invalidContactEmail', 'Please enter a valid email address')
      );
      return;
    }

    const nextContactSettings = {
      ...contactSettings,
      [fieldName]: nextValue
    };
    const payload = buildContactPayload(nextContactSettings);

    setSavingField(fieldName);

    try {
      const response = await setTenantSettings(SETTINGCODE, [payload]);
      if (!response?.data || response?.error) {
        throw new Error('Failed to save global contact settings.');
      }

      setContactSettings((currentContactSettings) => {
        const currentValue = currentContactSettings?.[fieldName]?.trim() || '';

        if (currentValue !== nextValue) {
          return currentContactSettings;
        }

        return {
          ...currentContactSettings,
          [fieldName]: nextValue
        };
      });
      setSavedContactSettings(nextContactSettings);
    } catch (error) {
      setContactSettings((currentContactSettings) => {
        const currentValue = currentContactSettings?.[fieldName]?.trim() || '';

        if (currentValue !== nextValue) {
          return currentContactSettings;
        }

        return {
          ...currentContactSettings,
          [fieldName]: previousValue
        };
      });
      ErrorToast(
        t('common:errorSavingSettings', {
          defaultValue: 'Error Saving Settings'
        })
      );
    } finally {
      setSavingField(null);
    }
  };

  return (
    <Box id={id} className='global-branding-panel' data-testid='branding-panel'>
      {isLoading ? (
        <Box className='global-branding-panel__content'>
          <Skeleton width='28%' height={24} />
          <Skeleton width='65%' height={18} />
          <Box className='global-branding-panel__fields'>
            <Skeleton variant='rectangular' width='100%' height={78} />
            <Skeleton variant='rectangular' width='100%' height={78} />
            <Skeleton variant='rectangular' width='100%' height={78} />
          </Box>
        </Box>
      ) : (
        <Box className='global-branding-panel__content'>
          <Box className='global-branding-panel__header'>
            <h6 className='text-lg-semibold global-branding-panel__title'>
              {t('settings:contactInformation', 'Contact information')}
            </h6>
            <p className='text-sm-regular global-branding-panel__description'>
              {t(
                'settings:globalContactInfoHelperText',
                "By default, the trip creator's details appear on trips and in emails. Update these fields to display different contact information across all trips."
              )}
            </p>
          </Box>

          <Box className='global-branding-panel__fields'>
            {fieldConfig.map((field) => {
              const helperId = `global-branding-panel-${field.name}-helper`;
              const errorId = `global-branding-panel-${field.name}-error`;
              const errorMessage = fieldErrors?.[field.name] || '';
              const describedBy = [
                field.helperText ? helperId : null,
                errorMessage ? errorId : null
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <Box className='global-branding-panel__field' key={field.name}>
                  <XGInput
                    id={`global-branding-panel-${field.name}-input`}
                    name={field.name}
                    type={field.type}
                    label={field.label}
                    placeholder={field.placeholder}
                    value={contactSettings?.[field.name] || ''}
                    onChange={(event) =>
                      handleFieldChange(field.name, event.target.value)
                    }
                    onBlur={() => handleFieldBlur(field.name)}
                    error={Boolean(errorMessage)}
                    hint={errorMessage}
                    icon={Boolean(errorMessage)}
                    customIcon={
                      errorMessage ? (
                        <WarningCircle color='#F04438' size={16} />
                      ) : null
                    }
                    inputProps={{
                      'aria-describedby': describedBy || undefined,
                      'aria-invalid': Boolean(errorMessage)
                    }}
                  />
                  {field.helperText ? (
                    <p
                      id={helperId}
                      className='text-sm-regular global-branding-panel__helper-text'
                    >
                      {field.helperText}
                    </p>
                  ) : null}
                  {errorMessage ? (
                    <span
                      id={errorId}
                      className='global-branding-panel__sr-only'
                    >
                      {errorMessage}
                    </span>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

BrandingPanel.propTypes = {
  id: PropTypes.string.isRequired
};

export default BrandingPanel;
